import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, ImageOff, Link2, Share2, Check } from "lucide-react";
import { ASSETS, LOCAL_PHOTOS } from "@/lib/constants";
import { fetchPhotos } from "@/lib/api";

const STATIC_PHOTOS = [
    { src: ASSETS.sanctuary, alt: "Sanctuary of Holy Trinity Kileleshwa", w: 1600, h: 900 },
    { src: ASSETS.massCelebration, alt: "", w: 1600, h: 900 },
    { src: ASSETS.procession, alt: "", w: 1600, h: 900 },
    { src: ASSETS.congregation, alt: "", w: 1600, h: 1600 },
    { src: ASSETS.holyWaterFont, alt: "Holy water font at the parish entrance", w: 1600, h: 900 },
    { src: ASSETS.baptismMural, alt: "", w: 1600, h: 1600 },
    { src: ASSETS.marianShrine, alt: "", w: 1200, h: 1600 },
    { src: ASSETS.steeple, alt: "", w: 1600, h: 1600 },
    { src: ASSETS.pewsBokeh, alt: "Quiet moment among the pews", w: 1600, h: 1600 },
];

/* -------- Lightbox -------- */
const Lightbox = ({ open, index, photos, onClose, onPrev, onNext, shareUrl }) => {
    const [copied, setCopied] = useState(false);
    const handleKey = useCallback(
        (e) => {
            if (!open) return;
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowRight") onNext();
            else if (e.key === "ArrowLeft") onPrev();
        },
        [open, onClose, onPrev, onNext]
    );

    useEffect(() => {
        if (!open) return undefined;
        document.addEventListener("keydown", handleKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = "";
        };
    }, [open, handleKey]);

    useEffect(() => { setCopied(false); }, [index]);

    const copyLink = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch { /* clipboard blocked, fall through */ }
    };

    const nativeShare = async (e) => {
        e.stopPropagation();
        const text = photos[index]?.alt || "Holy Trinity Kileleshwa";
        if (navigator.share) {
            try { await navigator.share({ title: "Holy Trinity Kileleshwa", text, url: shareUrl }); return; }
            catch { /* user cancelled or unsupported */ }
        }
        // Fallback: open WhatsApp share
        const wa = `https://wa.me/?text=${encodeURIComponent(text + ", " + shareUrl)}`;
        window.open(wa, "_blank", "noopener,noreferrer");
    };

    if (!open || index == null || !photos[index]) return null;
    const p = photos[index];

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: "rgba(10,6,6,0.92)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                data-testid="gallery-lightbox-close"
                aria-label="Close"
                className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full transition-colors"
                style={{ background: "rgba(61,46,44,0.75)", border: "1px solid rgba(201,162,39,0.5)", color: "#F3EDE4" }}
            >
                <X size={20} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                data-testid="gallery-lightbox-prev"
                aria-label="Previous photo"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-30 flex h-12 w-12 items-center justify-center rounded-full transition-transform hover:scale-105"
                style={{ background: "rgba(61,46,44,0.75)", border: "1px solid rgba(201,162,39,0.5)", color: "#F3EDE4" }}
            >
                <ChevronLeft size={22} />
            </button>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                data-testid="gallery-lightbox-next"
                aria-label="Next photo"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-30 flex h-12 w-12 items-center justify-center rounded-full transition-transform hover:scale-105"
                style={{ background: "rgba(61,46,44,0.75)", border: "1px solid rgba(201,162,39,0.5)", color: "#F3EDE4" }}
            >
                <ChevronRight size={22} />
            </button>
            <figure className="relative max-h-[92vh] max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img
                    src={p.src}
                    alt={p.alt}
                    className="max-h-[82vh] max-w-full rounded-2xl object-contain"
                    style={{ boxShadow: "0 40px 90px -30px rgba(154, 32, 38, 0.75)" }}
                />
                <figcaption
                    className="mt-3 max-w-2xl text-center text-sm sm:text-base font-serif italic px-4"
                    style={{ color: "#F3EDE4" }}
                >
                    {p.alt && <span>{p.alt} </span>}
                    <span className="text-xs opacity-70">{index + 1} / {photos.length}</span>
                </figcaption>
                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={copyLink}
                        data-testid="gallery-lightbox-copy-link"
                        aria-label="Copy share link"
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
                        style={{ background: copied ? "#C9A227" : "rgba(61,46,44,0.75)", color: copied ? "#3d2e2c" : "#F3EDE4", border: "1px solid rgba(201,162,39,0.5)" }}
                    >
                        {copied ? <><Check size={14} /> Link Copied</> : <><Link2 size={14} /> Copy Link</>}
                    </button>
                    <button
                        type="button"
                        onClick={nativeShare}
                        data-testid="gallery-lightbox-share"
                        aria-label="Share photo"
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
                        style={{ background: "rgba(61,46,44,0.75)", color: "#F3EDE4", border: "1px solid rgba(201,162,39,0.5)" }}
                    >
                        <Share2 size={14} /> Share
                    </button>
                </div>
            </figure>
        </div>
    );
};

/* -------- Photo tile -------- */
const PhotoTile = ({ p, i, onOpen }) => {
    const [failed, setFailed] = useState(false);
    return (
        <figure
            data-testid={`gallery-photo-${i}`}
            className="mb-5 break-inside-avoid rounded-2xl overflow-hidden border transition-all"
            style={{ borderColor: "rgba(189,110,114,0.40)", background: "#3d2e2c" }}
        >
            <button
                type="button"
                onClick={() => onOpen(i)}
                data-testid={`gallery-photo-open-${i}`}
                aria-label={`Open ${p.alt}`}
                className="group block w-full text-left focus:outline-none"
            >
                {failed ? (
                    <div
                        className="flex aspect-[4/3] w-full items-center justify-center"
                        style={{ background: "linear-gradient(135deg, rgba(154,32,38,0.6), rgba(46,34,32,0.7))", color: "#C9A227" }}
                    >
                        <ImageOff size={28} />
                    </div>
                ) : (
                    <img
                        src={p.src}
                        alt={p.alt}
                        className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03]"
                        loading="lazy"
                        decoding="async"
                        width={p.w}
                        height={p.h}
                        onError={() => setFailed(true)}
                    />
                )}
            </button>
            {p.alt && (
                <figcaption className="px-4 py-3 text-sm" style={{ color: "#F3EDE4" }}>
                    {p.alt}
                </figcaption>
            )}
        </figure>
    );
};

/* -------- Page -------- */
const Gallery = () => {
    const [uploaded, setUploaded] = useState([]);
    const [openIndex, setOpenIndex] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        let alive = true;
        fetchPhotos()
            .then((data) => alive && setUploaded(data))
            .catch(() => { /* silently fall back to static photos */ });
        return () => { alive = false; };
    }, []);

    const dynamicPhotos = uploaded.map((p) => ({
        src: p.url,
        alt: p.caption || "Holy Trinity Kileleshwa photo",
        w: p.width || 1600,
        h: p.height || 1200,
    }));

    // When the backend responds (bundled + uploaded live in the DB together),
    // trust that as the canonical list. Only fall back to the bundled static
    // arrays if the API is unreachable, so admins can edit every caption.
    const photos = dynamicPhotos.length > 0
        ? dynamicPhotos
        : [...LOCAL_PHOTOS, ...STATIC_PHOTOS];

    // Sync ?photo=<index> ↔ lightbox state so links are shareable.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const raw = params.get("photo");
        if (raw == null) { setOpenIndex(null); return; }
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 0 && n < photos.length) setOpenIndex(n);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, photos.length]);

    const openAt = (i) => {
        setOpenIndex(i);
        navigate(`?photo=${i}`, { replace: false });
    };
    const close = () => {
        setOpenIndex(null);
        navigate(location.pathname, { replace: false });
    };
    const prev = () => {
        setOpenIndex((i) => {
            if (i == null) return null;
            const n = (i - 1 + photos.length) % photos.length;
            navigate(`?photo=${n}`, { replace: true });
            return n;
        });
    };
    const next = () => {
        setOpenIndex((i) => {
            if (i == null) return null;
            const n = (i + 1) % photos.length;
            navigate(`?photo=${n}`, { replace: true });
            return n;
        });
    };

    const shareUrl = typeof window !== "undefined" && openIndex != null
        ? `${window.location.origin}${location.pathname}?photo=${openIndex}`
        : "";

    return (
        <div data-testid="gallery-page" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
            <span className="eyebrow">Parish Life</span>
            <h1 className="heading-display mt-2">Photos</h1>
            <p className="mt-4 max-w-2xl text-lg" style={{ color: "#F3EDE4" }}>
                Moments from our liturgies, devotions and community life at Holy Trinity Kileleshwa.
                Click any photo to open it full-screen, use the arrows or your keyboard to browse.
            </p>

            <div
                data-testid="gallery-grid"
                className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]"
            >
                {photos.map((p, i) => (
                    <PhotoTile key={`${p.src}-${i}`} p={p} i={i} onOpen={openAt} />
                ))}
            </div>

            <p className="mt-8 text-sm italic" style={{ color: "#B9B1A4" }}>
                Follow @holytrinitykile on Instagram and our Facebook page for more weekly moments.
            </p>

            <Lightbox
                open={openIndex !== null}
                index={openIndex}
                photos={photos}
                onClose={close}
                onPrev={prev}
                onNext={next}
                shareUrl={shareUrl}
            />
        </div>
    );
};

export default Gallery;
